import { type FC, type RefObject, useMemo } from 'react';

import type { BodyScrollEvent, ColDef } from 'ag-grid-community';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import {
  IconDownload,
  IconEye,
  IconLoader2,
} from '@tabler/icons-react';
import {
  ButtonFieldIcon,
  Tooltip as TooltipField,
  TooltipBody,
} from '@wk/components-react16';

import type { Evaluation } from '@/types';
import { cn, userLocaleTimezone } from '@/lib/utils';
import { renderSource } from '@/modules/utils/cell-renderers';

import {
  formatEvaluationGeneratedAt,
  getScoreIndicatorColorClass,
  sanitizeEvaluationActionId,
} from './ApplicationDetailsPage.utils';

interface ApplicationDetailsGridProps {
  activeEvaluationId: string | null;
  evaluationRows: Evaluation[];
  gridContainerRef: RefObject<HTMLDivElement | null>;
  gridRef: RefObject<AgGridReact<Evaluation> | null>;
  isExportingCsv: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  onBodyScroll: (event: BodyScrollEvent<Evaluation>) => void;
  onDownloadEvaluation: (evaluation: Evaluation) => void;
  onNavigateToEvaluation: (evaluationId: string) => void;
}

const ApplicationDetailsGrid: FC<ApplicationDetailsGridProps> = ({
  activeEvaluationId,
  evaluationRows,
  gridContainerRef,
  gridRef,
  isExportingCsv,
  isFetchingNextPage,
  isLoading,
  onBodyScroll,
  onDownloadEvaluation,
  onNavigateToEvaluation,
}) => {
  const columnDefs = useMemo<ColDef<Evaluation>[]>(
    () => [
      {
        headerName: 'Generated At',
        field: 'generatedAt',
        sortable: false,
        minWidth: 250,
        flex: 1,
        cellRenderer: (params: CustomCellRendererProps<Evaluation>) => (
          <div className="flex items-center gap-2">
            <p>
              {`${formatEvaluationGeneratedAt(params.value)} ${userLocaleTimezone()}`}
            </p>
          </div>
        ),
      },
      {
        headerName: 'Source',
        field: 'platform',
        sortable: false,
        cellRenderer: (params: CustomCellRendererProps<Evaluation>) => {
          return renderSource(params, { renderName: true });
        },
      },
      {
        headerName: 'Score',
        field: 'score',
        sortable: false,
        cellRenderer: (params: CustomCellRendererProps<Evaluation>) => (
          <div className="flex items-center gap-2">
            <div
              className={cn([
                'w-2 h-2 rounded-full',
                getScoreIndicatorColorClass(params.value),
              ])}
            />
            <p>
              {params.value === null || params.value === undefined
                ? 'N/A'
                : `${params.value}%`}
            </p>
          </div>
        ),
      },
      {
        headerName: 'Total Checkpoints',
        field: 'totalCheckpoints',
      },
      {
        headerName: 'Actions',
        pinned: 'right',
        sortable: false,
        minWidth: 250,
        cellRenderer: (params: CustomCellRendererProps<Evaluation>) => {
          const evaluation = params.data;

          if (!evaluation) {
            return null;
          }

          const sanitizedId = sanitizeEvaluationActionId(evaluation.id);
          const viewActionId = `view-evaluation-${sanitizedId}`;
          const exportActionId = `export-evaluation-${sanitizedId}`;

          return (
            <div className="flex items-center gap-2">
              <TooltipField
                position="right"
                targetSelector={`#${viewActionId}`}
              >
                <TooltipBody slot="tooltipBody">View Evaluation</TooltipBody>
              </TooltipField>
              <ButtonFieldIcon size="small16px">
                <button
                  id={viewActionId}
                  onClick={() => onNavigateToEvaluation(evaluation.id)}
                >
                  <IconEye size={18} />
                </button>
              </ButtonFieldIcon>
              <TooltipField
                position="right"
                targetSelector={`#${exportActionId}`}
              >
                <TooltipBody slot="tooltipBody">Download Report</TooltipBody>
              </TooltipField>
              <ButtonFieldIcon size="small16px">
                <button
                  id={exportActionId}
                  disabled={
                    isExportingCsv && activeEvaluationId === evaluation.id
                  }
                  onClick={() => onDownloadEvaluation(evaluation)}
                >
                  {isExportingCsv && activeEvaluationId === evaluation.id ? (
                    <IconLoader2 className="animate-spin" size={18} />
                  ) : (
                    <IconDownload size={18} />
                  )}
                </button>
              </ButtonFieldIcon>
            </div>
          );
        },
      },
    ],
    [
      activeEvaluationId,
      isExportingCsv,
      onDownloadEvaluation,
      onNavigateToEvaluation,
    ],
  );

  return (
    <div className="flex flex-1 w-full h-[calc(100%-3rem)] max-sm:h-auto cg-ag-grid-field ag-theme-balham">
      <div
        className="application-details-grid-scroll relative h-full w-full max-sm:h-[52vh] max-sm:min-h-[320px] max-sm:overflow-x-auto"
        ref={gridContainerRef}
      >
        <div className="h-full w-full max-sm:min-w-[940px]">
          <AgGridReact
            animateRows={false}
            autoSizeStrategy={{
              type: 'fitGridWidth',
              defaultMinWidth: 250,
            }}
            loading={isLoading || isFetchingNextPage}
            onBodyScroll={onBodyScroll}
            rowData={evaluationRows}
            ref={gridRef}
            columnDefs={columnDefs}
          />
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetailsGrid;
